import { BrowserModule } from '@angular/platform-browser';
import { inject, Injector, NgModule } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateParser } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './routing/routing.module';
import { ServiceLocator } from '../../../workbench/src/app/locator.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { RouteReuseStrategy } from '@angular/router';
import { MessageService, SharedModule } from 'primeng/api';
import { SelfServeModule } from '../../../components/src/lib/self-serve/self-serve.module';
import { SystemStatusModule } from '../../../components/src/lib/system-status/system-status.module';
import { ZiroToastComponent } from '../../../components/src/lib/shared/toast/toast.component';
import { HelpdeskModule } from '../../../components/src/lib/helpdesk/helpdesk.module';
import { LoginModule } from '../../../components/src/lib/login/login.module';
import { HomeModule } from '../../../components/src/lib/home/home.module';
import { CdrDumpModule } from '../../../components/src/lib/cdr-dump/cdr-dump.module';
import { PipesModule } from '../../../components/src/lib/shared/pipes/pipes.module';
import { ReleaseNotesModule } from '../../../components/src/lib/shared/release-notes/release-notes.module';
import { EulaModule } from '../../../components/src/lib/shared/eula/eula.module';
import { httpInterceptorProvider } from '../../../components/src/lib/shared/services/http-interceptor.service';
import { globalErrorProvider } from '../../../components/src/lib/shared/services/global-error-handler.service';
import { AppRouteReuseStrategy } from '../../../components/src/lib/route-reuse-strategy';
import { ActivateDeskphoneRouteGuardService } from '../../../components/src/lib/shared/services/activate-deskphone-route-guard.service';
import { MigrationsModule } from '../../../components/src/lib/migrations/migrations.module';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Lara from '@primeng/themes/lara';
import { ZiroTranslateParser } from '../../../components/src/lib/translate-parser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DatatableColumnWidthPipe } from '../../../components/src/lib/shared/tables/datatable/datatable-column-width.pipe';
import { DynamicPipe } from '../../../components/src/lib/shared/pipes/dynamic.pipe';
import { StripHtmlPipe } from '../../../components/src/lib/shared/pipes/strip-html.pipe';

// import { primeNgPreset } from './primeNgPreset';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/static/locale/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    SharedModule,
    NgbModule,
    SelfServeModule,
    SystemStatusModule,
    NgSelectModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      parser: {
        provide: TranslateParser,
        useClass: ZiroTranslateParser,
      },
    }),
    ZiroToastComponent,
    HelpdeskModule,
    LoginModule,
    MigrationsModule,
    HomeModule,
    CdrDumpModule,
    PipesModule,
    ReleaseNotesModule,
    EulaModule,
  ],
  providers: [
    httpInterceptorProvider,
    globalErrorProvider,
    {
      provide: RouteReuseStrategy,
      useClass: AppRouteReuseStrategy,
    },
    ActivateDeskphoneRouteGuardService,
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: definePreset(Lara, {
          components: {
            toast: {
              root: {
                borderWidth: '0',
              },
              colorScheme: {
                light: {
                  info: {
                    background: 'rgba(32, 32, 32, 0.4)',
                    borderColor: 'transparent',
                    color: '#ffffff',
                    detailColor: '#ffffff',
                  },
                  success: {
                    background: 'rgba(66, 215, 64, 0.4)',
                    borderColor: 'transparent',
                    color: '#ffffff',
                    detailColor: '#ffffff',
                  },
                  warn: {
                    background: 'rgba(253, 122, 61, 0.4)',
                    borderColor: 'transparent',
                    color: '#ffffff',
                    detailColor: '#ffffff',
                  },
                  error: {
                    background: 'rgba(251, 70, 86, 0.4)',
                    borderColor: 'transparent',
                    color: '#ffffff',
                    detailColor: '#ffffff',
                  },
                },
              },
            },
          },
        }),
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
          darkModeSelector: false,
        },
      },
    }),
    DatatableColumnWidthPipe,
    DynamicPipe,
    StripHtmlPipe,
  ],
})
export class AppModule {
  private injector = inject(Injector);

  constructor() {
    ServiceLocator.injector = this.injector;
  }
}
